/**
 * DynamicPage — renders a CMS-managed page (published or preview).
 * Route: /:slug  OR  /preview/:token
 */
import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import SectionRenderer from "../components/SectionRenderer";
import "./HomePage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const DynamicPage = ({ previewMode = false }) => {
  const { slug, token } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [navigation, setNavigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Contact form state (used if the page has a contact_form section)
  const [formData, setFormData] = useState({ instituteName: "", yourName: "", email: "", phone: "", message: "" });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const url = previewMode ? `${API}/public/preview/${token}` : `${API}/public/page/${slug}`;
    Promise.all([
      axios.get(url).then((r) => r.data),
      axios.get(`${API}/public/site-settings`).then((r) => r.data),
      axios.get(`${API}/public/navigation`).then((r) => r.data),
    ])
      .then(([p, s, n]) => { setPage(p); setSettings(s); setNavigation(n); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug, token, previewMode]);

  useEffect(() => {
    if (!page) return;
    document.title = page.seo?.title || page.title;
    const applyMeta = (name, content, isProp = false) => {
      const attr = isProp ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content || "");
    };
    applyMeta("description", page.seo?.description || page.description);
    applyMeta("og:title", page.seo?.title || page.title, true);
    applyMeta("og:description", page.seo?.description || page.description, true);
    if (page.seo?.og_image_url) applyMeta("og:image", page.seo.og_image_url.startsWith("/") ? `${BASE}${page.seo.og_image_url}` : page.seo.og_image_url, true);
    if (page.seo?.robots) applyMeta("robots", page.seo.robots);
    if (page.seo?.canonical_url) {
      let link = document.querySelector("link[rel='canonical']");
      if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
      link.href = page.seo.canonical_url;
    }
    return () => { document.title = "Prepify"; };
  }, [page]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/public/contact-submit`, {
        institute_name: formData.instituteName,
        your_name: formData.yourName,
        email: formData.email,
        phone: formData.phone,
        content_type: "",
        message: formData.message,
      });
      setSubmitStatus("success");
      setFormData({ instituteName: "", yourName: "", email: "", phone: "", message: "" });
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 6000);
    }
  };

  if (loading) return <div className="prepify-v2" style={{ padding: 200, textAlign: "center", color: "#737373" }}>Loading…</div>;
  if (notFound || !page) return (
    <div className="prepify-v2">
      <div style={{ padding: "200px 24px", textAlign: "center" }}>
        <h1 className="sec-title">Page not found</h1>
        <Link to="/" className="btn-pill-primary" style={{ marginTop: 20, display: "inline-flex" }}>Back home</Link>
      </div>
    </div>
  );

  const brand = settings?.brand_name || "prepify";
  const headerItems = (navigation?.header_items || []).filter((i) => i.is_visible !== false).sort((a, b) => a.order - b.order);
  const footerCols = (navigation?.footer_columns || []).sort((a, b) => a.order - b.order);

  const renderNavLink = (item) => {
    const isInternal = item.href?.startsWith("/") && !item.is_external;
    const target = item.open_in_new_tab ? "_blank" : undefined;
    const rel = item.open_in_new_tab ? "noreferrer" : undefined;
    if (item.href?.startsWith("#")) {
      return <button key={item.id} onClick={() => { window.location.href = `/${item.href}`; }}>{item.label}</button>;
    }
    if (isInternal) return <Link key={item.id} to={item.href} style={{ background: "none", border: "none", color: "var(--ink-3)", fontFamily: "Satoshi,sans-serif", fontSize: "0.875rem", fontWeight: 500, padding: "8px 14px", borderRadius: 100, textDecoration: "none" }}>{item.label}</Link>;
    return <a key={item.id} href={item.href} target={target} rel={rel} style={{ background: "none", border: "none", color: "var(--ink-3)", fontFamily: "Satoshi,sans-serif", fontSize: "0.875rem", fontWeight: 500, padding: "8px 14px", borderRadius: 100, textDecoration: "none" }}>{item.label}</a>;
  };

  const renderFooterLink = (item) => {
    const isInternal = item.href?.startsWith("/") && !item.is_external;
    const target = item.open_in_new_tab ? "_blank" : undefined;
    const rel = item.open_in_new_tab ? "noreferrer" : undefined;
    if (isInternal) return <Link key={item.id} to={item.href}>{item.label}</Link>;
    if (item.href?.startsWith("#")) return <a key={item.id} href={`/${item.href}`}>{item.label}</a>;
    return <a key={item.id} href={item.href} target={target} rel={rel}>{item.label}</a>;
  };

  const sortedSections = [...(page.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="prepify-v2">
      {previewMode && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 2000, background: "#F59E0B", color: "#0A0A0A", textAlign: "center", fontSize: 13, fontWeight: 600, padding: "6px 16px" }}>
          ✦ Preview Mode — This page is not live yet
        </div>
      )}
      {/* NAV */}
      <nav className="nav-pill" style={previewMode ? { top: 40 } : {}}>
        <div className="nav-pill-inner">
          <Link to="/" className="nav-logo" style={{ textDecoration: "none" }}>
            {settings?.logo_url ? <img src={settings.logo_url.startsWith("/") ? `${BASE}${settings.logo_url}` : settings.logo_url} alt={brand} style={{ height: 24 }} /> : (<><span className="nav-logo-dot" />{brand}</>)}
          </Link>
          <div className="nav-links">{headerItems.map(renderNavLink)}</div>
          <div className="nav-actions">
            <Link to="/login" className="nav-login">Staff Login</Link>
            <Link to="/#contact" className="btn-pill-primary nav-cta">Partner with us <ArrowUpRight size={16} strokeWidth={2.5} /></Link>
          </div>
        </div>
      </nav>

      {/* Page hero (title + description fallback when no hero section) */}
      {!sortedSections.some((s) => s.type === "hero") && (
        <section style={{ padding: "140px 24px 40px", background: "linear-gradient(180deg,#fff 0%,var(--mint-50) 100%)", textAlign: "center" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <h1 className="sec-title" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>{page.title}</h1>
            {page.description && <p className="sec-desc" style={{ marginTop: 20 }}>{page.description}</p>}
          </div>
        </section>
      )}

      {/* SECTIONS */}
      {sortedSections.map((section) => (
        <SectionRenderer
          key={section.id}
          section={section}
          onSubmit={handleContactSubmit}
          submitStatus={submitStatus}
          isSubmitting={isSubmitting}
          formData={formData}
          setFormData={setFormData}
        />
      ))}

      {/* FOOTER */}
      <footer className="footer-v2">
        <div className="footer-v2-inner">
          <div className="footer-v2-top">
            <div className="footer-v2-brand">
              <div className="footer-v2-logo"><span className="footer-v2-dot" />{brand}</div>
              <p>{settings?.tagline || ""}</p>
            </div>
            <div className="footer-v2-cols">
              {footerCols.slice(0, 3).map((col) => (
                <div key={col.id} className="footer-v2-col">
                  <h5>{col.heading}</h5>
                  {(col.items || []).filter((i) => i.is_visible !== false).sort((a, b) => a.order - b.order).map(renderFooterLink)}
                </div>
              ))}
            </div>
          </div>
          <div className="footer-watermark">{brand}</div>
          <div className="footer-v2-bottom">
            <span>© {new Date().getFullYear()} {brand}. All rights reserved.</span>
            <span>{settings?.footer_note}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DynamicPage;
